import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";

import Title from "../Components/Title";
import { Link } from "react-router-dom";
import Button from "../Components/Button";
import Tabs from "../Components/Tabs";
import UserMergeModal from "../Components/UserMergeModal";

function Admin({ AdminStore }) {
  useEffect(() => {
    AdminStore.loadAppUpdates();
    AdminStore.loadUsers();
    AdminStore.loadVersions();
  }, [AdminStore]);

  const tabs = [
    {
      id: "users",
      label: "Users",
      icon: "👥",
      content: <UsersTable loaded={AdminStore.usersLoaded} list={AdminStore.users} />,
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: "🔔",
      content: (
        <NotificationsForm
          users={AdminStore.users.map((u) => u.username)}
          onSubmit={(to, title, body) => {
            AdminStore.pushNotification(to, title, body);
          }}
        />
      ),
    },
    {
      id: "updates",
      label: "App Updates",
      icon: "📱",
      content: (
        <div className="space-y-8">
          <PushUpdateForm
            activity={AdminStore.appUpdateUpload}
            onSubmit={(updateFile, changeLog) => {
              AdminStore.pushAppUpdate(updateFile, changeLog);
            }}
          />
          <UpdatesTable
            loaded={AdminStore.appUpdatesLoaded}
            list={AdminStore.appUpdates}
          />
        </div>
      ),
    },
    {
      id: "versions",
      label: "Versions",
      icon: "ℹ️",
      content: (
        <VersionDisplay
          loaded={AdminStore.versionsLoaded}
          serverVersion={AdminStore.serverVersion}
          clientVersion={AdminStore.clientVersion}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full bg-primary p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text">
          Admin Dashboard
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Manage app updates, send notifications, and view users
        </p>
      </div>
      <div className="flex-1 overflow-hidden">
        <Tabs tabs={tabs} defaultTab="users" />
      </div>
    </div>
  );
}

const PushUpdateForm = ({ activity, onSubmit }) => {
  const [changeLog, setChangeLog] = useState("");
  const [updateFile, setUpdateFile] = useState(null);
  return (
    <div className="bg-secondary rounded-lg p-6 shadow-md">
      <Title label="Push New Update" />
      {activity && (
        <div className="mt-4 flex items-center gap-3 text-accent">
          <div className="animate-spin h-5 w-5 border-2 border-accent border-t-transparent rounded-full"></div>
          <span>Pushing app update...</span>
        </div>
      )}
      {!activity && (
        <div className="flex flex-col gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              APK File
            </label>
            <input
              type="file"
              name="updateFile"
              onChange={(e) => {
                setUpdateFile(e.target.files[0]);
              }}
              accept=".apk, application/vnd.android.package-archive"
              className="w-full text-text file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-opacity-80 file:cursor-pointer"
            />
            {updateFile && (
              <p className="mt-2 text-sm text-gray-400">
                Selected: {updateFile.name}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              Changelog
            </label>
            <textarea
              className="w-full h-32 p-3 rounded-md bg-primary text-text border border-neutral-700 focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
              name="changeLog"
              placeholder="Describe what's new in this update..."
              onChange={(e) => {
                setChangeLog(e.target.value);
              }}
              value={changeLog}
            />
          </div>
          <Button
            onClick={() => {
              if (!updateFile) {
                alert("please select an app file to push");
                return;
              }
              if (changeLog === "") {
                alert("please fill in the changelog");
                return;
              }
              onSubmit(updateFile, changeLog);
            }}
            size="lg"
          >
            Push Update
          </Button>
        </div>
      )}
    </div>
  );
};

const UpdatesTable = ({ loaded, list }) => {
  return (
    <div className="bg-secondary rounded-lg p-6 shadow-md">
      <Title label="Available Updates" />
      {!loaded && (
        <div className="mt-4 flex items-center gap-3 text-text">
          <div className="animate-spin h-5 w-5 border-2 border-text border-t-transparent rounded-full"></div>
          <span>Loading updates...</span>
        </div>
      )}
      {loaded && list.length === 0 && (
        <div className="mt-4 text-center py-8 text-gray-400">
          No updates available yet
        </div>
      )}
      {loaded && list.length > 0 && (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-left text-text border-collapse">
            <thead>
              <tr className="bg-primary">
                <th className="p-3 border border-neutral-700 font-semibold">Changelog</th>
                <th className="p-3 border border-neutral-700 font-semibold">File Name</th>
                <th className="p-3 border border-neutral-700 font-semibold">Size</th>
                <th className="p-3 border border-neutral-700 font-semibold">Created At</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.versionCode} className="hover:bg-primary transition-colors">
                  <td className="p-3 border border-neutral-700">
                    <pre className="text-sm whitespace-pre-wrap font-sans">{item.changeLog}</pre>
                  </td>
                  <td className="p-3 border border-neutral-700">{item.fileName}</td>
                  <td className="p-3 border border-neutral-700">{item.size}</td>
                  <td className="p-3 border border-neutral-700">{item.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const NotificationsForm = ({ users, onSubmit }) => {
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [to, setTo] = useState("all");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!title || !body) {
      alert("Please fill in both title and content");
      return;
    }
    setSending(true);
    try {
      await onSubmit(to, title, body);
      setTitle("");
      setBody("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-secondary rounded-lg p-6 shadow-md max-w-2xl">
      <Title label="Send Notification" />
      <p className="text-sm text-gray-400 mt-2">
        Send push notifications to users
      </p>
      <div className="flex flex-col gap-4 mt-6">
        <div>
          <label className="block text-sm font-medium text-text mb-2">
            Recipients
          </label>
          <select
            onChange={(e) => {
              setTo(e.target.value);
            }}
            value={to}
            className="w-full p-3 rounded-md bg-primary text-text border border-neutral-700 focus:ring-2 focus:ring-accent focus:border-transparent"
          >
            <option key="all" value="all">
              📢 Everybody
            </option>
            {users.map((user) => (
              <option key={user} value={user}>
                👤 {user}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-2">
            Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Notification title"
            className="w-full p-3 rounded-md bg-primary text-text border border-neutral-700 focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-2">
            Content
          </label>
          <textarea
            className="w-full h-32 p-3 rounded-md bg-primary text-text border border-neutral-700 focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
            placeholder="Notification content"
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
          />
        </div>
        <Button
          onClick={handleSend}
          disabled={sending}
          size="lg"
        >
          {sending ? "Sending..." : "Send Notification"}
        </Button>
      </div>
    </div>
  );
};

const UsersTable = ({ loaded, list }) => {
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [sourceUser, setSourceUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleMergeClick = (user) => {
    setSourceUser(user);
    setIsMergeModalOpen(true);
  };

  const handleMergeComplete = (result) => {
    setSuccessMessage(result.message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  return (
    <div className="bg-secondary rounded-lg p-6 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Title label="Users" />
          {loaded && (
            <p className="text-sm text-gray-400 mt-1">
              {list.length} {list.length === 1 ? 'user' : 'users'} registered
            </p>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-900 border-l-4 border-green-500 rounded">
          <p className="text-green-200 text-sm">{successMessage}</p>
        </div>
      )}

      {!loaded && (
        <div className="flex items-center gap-3 text-text">
          <div className="animate-spin h-5 w-5 border-2 border-text border-t-transparent rounded-full"></div>
          <span>Loading users...</span>
        </div>
      )}
      {loaded && list.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          No users registered yet
        </div>
      )}
      {loaded && list.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-text border-collapse">
            <thead>
              <tr className="bg-primary">
                <th className="p-3 border border-neutral-700 font-semibold">Username</th>
                <th className="p-3 border border-neutral-700 font-semibold">Email</th>
                <th className="p-3 border border-neutral-700 font-semibold">Status</th>
                <th className="p-3 border border-neutral-700 font-semibold">Created At</th>
                <th className="p-3 border border-neutral-700 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.username} className="hover:bg-primary transition-colors">
                  <td className="p-3 border border-neutral-700">
                    <Link
                      to={`/app/u/${item.username}`}
                      className="text-accent hover:underline font-medium"
                    >
                      {item.username}
                    </Link>
                  </td>
                  <td className="p-3 border border-neutral-700">{item.email}</td>
                  <td className="p-3 border border-neutral-700">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.active
                        ? 'bg-green-900 text-green-200'
                        : 'bg-red-900 text-red-200'
                    }`}>
                      {item.active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td className="p-3 border border-neutral-700">{item.createdAt}</td>
                  <td className="p-3 border border-neutral-700">
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => handleMergeClick(item)}
                    >
                      Merge
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserMergeModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        sourceUser={sourceUser}
        users={list}
        onMergeComplete={handleMergeComplete}
      />
    </div>
  );
};

const VersionDisplay = ({ loaded, serverVersion, clientVersion }) => {
  return (
    <div className="bg-secondary rounded-lg p-6 shadow-md max-w-2xl">
      <Title label="Application Versions" />
      <p className="text-sm text-gray-400 mt-2">
        Current versions of client and server components
      </p>
      {!loaded && (
        <div className="mt-6 flex items-center gap-3 text-text">
          <div className="animate-spin h-5 w-5 border-2 border-text border-t-transparent rounded-full"></div>
          <span>Loading version information...</span>
        </div>
      )}
      {loaded && (
        <div className="mt-6 space-y-4">
          <div className="bg-primary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text">
                  Web Client
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Frontend application version
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-accent text-white">
                  v{clientVersion || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-primary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text">
                  Server
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Backend server version
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-accent text-white">
                  v{serverVersion || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default inject("AdminStore")(observer(Admin));
